/**
 * <ReleasesView> — from "approved fixes" to a version every organization can
 * upgrade to.
 *
 * Three parts, top to bottom in the order a project manager works them:
 *
 *   1. READY TO RELEASE — the pm-approved issues no release has taken yet.
 *      Tick some (default: all) and create a DRAFT. The server proposes the
 *      version (patch for fixes, minor when a feature is in) and the AI writes
 *      the notes in the background.
 *   2. DRAFTS — edit the version, title and per-issue notes, then CUT: the
 *      server merges, tests, tags, pushes and snapshots, and this view follows
 *      its progress log.
 *   3. HISTORY — every release with its push status and GitHub links, and a
 *      failed one with the reason, so "did it go out?" never needs a terminal.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  AlertTriangle, ChevronDown, ChevronRight, ExternalLink, GitPullRequest, PackageCheck, Rocket,
  Save, Tag, Trash2,
} from 'lucide-react'

import type {
  IssueSummary, Release, ReleaseNote, SupportMeta, SupportNotify, SupportRuntime,
} from './contract'
import {
  Card, Empty, ErrorLine, Pill, Spinner, SupportScope, btn, errorText, fmtDateTime, inputStyle,
  labelStyle, mutedText, stateLabel, vocabLabel, type Tone,
} from './ui'
import { noteGroups } from './options'

export interface ReleasesViewProps {
  runtime: SupportRuntime
  meta: SupportMeta
  notify?: SupportNotify
  /** Open an issue (the host shows it on the board). */
  onOpenIssue?: (issueNumber: number) => void
  /** Refresh cadence while a release is building; 0 disables. */
  pollMs?: number
}


function releaseTone(status: string): Tone {
  if (status === 'released') return 'success'
  if (status === 'failed') return 'error'
  if (status === 'building') return 'accent'
  if (status === 'draft') return 'warning'
  return 'neutral'
}

export default function ReleasesView({ runtime, meta, notify, onOpenIssue, pollMs = 5000 }: ReleasesViewProps) {
  const [releases, setReleases] = useState<Release[] | null>(null)
  const [ready, setReady] = useState<IssueSummary[]>([])
  const [error, setError] = useState('')
  const isPm = meta.me.capacities.includes('pm')

  const load = useCallback(async (quiet = false) => {
    if (!runtime.listReleases) return
    try {
      const res = await runtime.listReleases()
      setReleases(res.releases || [])
      setReady(res.ready || [])
      setError('')
    } catch (e) {
      if (!quiet) setError(errorText(e, 'Releases could not be loaded.'))
    }
  }, [runtime])

  useEffect(() => { void load() }, [load])

  const building = (releases || []).some((r) => r.status === 'building')
  useEffect(() => {
    if (!pollMs || !building) return undefined
    const id = window.setInterval(() => { void load(true) }, pollMs)
    return () => window.clearInterval(id)
  }, [pollMs, building, load])

  if (!runtime.listReleases) {
    return <SupportScope><Empty>Releases are managed on the platform.</Empty></SupportScope>
  }

  const drafts = (releases || []).filter((r) => r.status === 'draft' || r.status === 'building')
  const history = (releases || []).filter((r) => r.status !== 'draft' && r.status !== 'building')

  return (
    <SupportScope>
      <ErrorLine message={error} onRetry={() => { void load() }} />
      {releases === null && !error && <div style={{ display: 'flex', gap: 8 }}><Spinner /> Loading releases…</div>}

      {releases !== null && (
        <>
          <ReadyList ready={ready} runtime={runtime} meta={meta} isPm={isPm} notify={notify}
            onOpenIssue={onOpenIssue} onCreated={() => { void load() }} />

          {drafts.map((r) => (
            <DraftEditor key={r.id} release={r} runtime={runtime} isPm={isPm} notify={notify}
              groups={noteGroups(meta)}
              onChanged={() => { void load(true) }} />
          ))}

          <Card title="Release history" icon={<PackageCheck size={15} />}>
            {history.length === 0 ? <div style={mutedText}>Nothing released yet.</div> : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {history.map((r) => <HistoryRow key={r.id} release={r} meta={meta} onOpenIssue={onOpenIssue} />)}
              </ul>
            )}
          </Card>
        </>
      )}
    </SupportScope>
  )
}

// ── ready to release ───────────────────────────────────────────────────────

function ReadyList({ ready, runtime, meta, isPm, notify, onOpenIssue, onCreated }: {
  ready: IssueSummary[]
  runtime: SupportRuntime
  meta: SupportMeta
  isPm: boolean
  notify?: SupportNotify
  onOpenIssue?: (n: number) => void
  onCreated: () => void
}) {
  const [picked, setPicked] = useState<Set<number>>(new Set())
  const [kind, setKind] = useState('')
  const [version, setVersion] = useState('')
  const [title, setTitle] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  // Default: everything that is ready — the usual release is "all of it".
  const readyKey = ready.map((r) => r.number).join(',')
  useEffect(() => { setPicked(new Set(ready.map((r) => r.number))) }, [readyKey]) // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    if (!runtime.createRelease) return
    setBusy(true)
    setError('')
    try {
      const rel = await runtime.createRelease({
        issue_numbers: Array.from(picked), kind: kind || undefined,
        version: version.trim() || undefined, title: title.trim() || undefined,
      })
      notify?.success(`Draft v${rel.version} created — the AI is writing its notes.`)
      setVersion('')
      setTitle('')
      onCreated()
    } catch (e) {
      setError(errorText(e, 'The draft could not be created.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title={`Ready to release (${ready.length})`} icon={<Rocket size={15} />}>
      {ready.length === 0 ? (
        <div style={mutedText}>No approved fixes are waiting. Issues land here once a project manager approves them.</div>
      ) : (
        <>
          <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 10px' }}>
            {ready.map((i) => (
              <li key={i.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <input type="checkbox" checked={picked.has(i.number)} disabled={!isPm}
                  aria-label={`Include #${i.number}`}
                  onChange={(e) => setPicked((cur) => {
                    const next = new Set(cur)
                    if (e.target.checked) next.add(i.number)
                    else next.delete(i.number)
                    return next
                  })} />
                <span style={{ ...mutedText, fontWeight: 700 }}>#{i.number}</span>
                <span style={{ flex: 1, minWidth: 0 }}>{i.title}</span>
                <Pill>{vocabLabel(meta, 'issue_types', i.type)}</Pill>
                {onOpenIssue && (
                  <button type="button" style={{ ...btn('ghost', true), padding: '0 0.45rem' }}
                    onClick={() => onOpenIssue(i.number)}>Open</button>
                )}
              </li>
            ))}
          </ul>
          {isPm && runtime.createRelease ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '140px 140px 1fr', gap: 8, marginBottom: 8 }}>
                <div>
                  <label style={labelStyle}>Kind</label>
                  <select value={kind} onChange={(e) => setKind(e.target.value)} style={inputStyle}>
                    <option value="">Suggest for me</option>
                    <option value="patch">Patch</option>
                    <option value="minor">Minor</option>
                    <option value="major">Major</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Version</label>
                  <input value={version} onChange={(e) => setVersion(e.target.value)} style={inputStyle}
                    placeholder="auto" />
                </div>
                <div>
                  <label style={labelStyle}>Title (optional)</label>
                  <input value={title} onChange={(e) => setTitle(e.target.value)} style={inputStyle}
                    maxLength={255} />
                </div>
              </div>
              <ErrorLine message={error} />
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button type="button" style={btn('primary')} disabled={busy || picked.size === 0}
                  onClick={() => { void create() }}>
                  {busy ? <Spinner size={13} /> : <Tag size={13} />} Create draft release ({picked.size})
                </button>
              </div>
            </>
          ) : (
            <div style={mutedText}>A project manager bundles these into a release.</div>
          )}
        </>
      )}
    </Card>
  )
}

// ── draft editor ───────────────────────────────────────────────────────────

function ProgressLog({ release }: { release: Release }) {
  const log = release.progress?.log || []
  if (!log.length) return null
  return (
    <ol style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: '0.8425rem' }}>
      {log.map((e, i) => (
        <li key={i} style={{ marginBottom: 2 }}>
          <strong>{e.step}</strong>
          {e.detail ? <> — {String(e.detail)}</> : null}
          {typeof e.issue === 'number' ? <> (#{e.issue})</> : null}
          <span style={{ ...mutedText, marginLeft: 6 }}>{e.at ? new Date(e.at).toLocaleTimeString() : ''}</span>
        </li>
      ))}
    </ol>
  )
}

function DraftEditor({ release, runtime, isPm, notify, onChanged, groups }: {
  release: Release
  /** The note groups the server's notes writer and renderer use. */
  groups: string[]
  runtime: SupportRuntime
  isPm: boolean
  notify?: SupportNotify
  onChanged: () => void
}) {
  const editable = release.status === 'draft' && isPm && !!runtime.patchRelease
  const [version, setVersion] = useState(release.version)
  const [kind, setKind] = useState(release.kind || 'patch')
  const [title, setTitle] = useState(release.title || '')
  const [notes, setNotes] = useState<ReleaseNote[]>(release.notes || [])
  const [notesMd, setNotesMd] = useState(release.notes_md || '')
  const [mdTouched, setMdTouched] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [busy, setBusy] = useState<'' | 'save' | 'cut' | 'discard'>('')
  const [error, setError] = useState('')

  // The AI rewrites a fresh draft's notes in the background; adopt its version
  // until the manager starts editing, never over their edits.
  useEffect(() => {
    if (dirty) return
    setVersion(release.version)
    setKind(release.kind || 'patch')
    setTitle(release.title || '')
    setNotes(release.notes || [])
    setNotesMd(release.notes_md || '')
  }, [release, dirty])

  const setNote = (i: number, patch: Partial<ReleaseNote>) => {
    setNotes((cur) => cur.map((n, j) => (j === i ? { ...n, ...patch } : n)))
    setDirty(true)
  }

  const save = async (): Promise<boolean> => {
    if (!runtime.patchRelease) return false
    setBusy('save')
    setError('')
    try {
      await runtime.patchRelease(release.id, {
        version: version.trim() || undefined, kind, title,
        notes,
        // Untouched markdown is regenerated from the per-issue notes by the
        // server; an edited one is kept as written.
        ...(mdTouched ? { notes_md: notesMd } : {}),
      })
      setDirty(false)
      setMdTouched(false)
      notify?.success(`Draft v${version} saved.`)
      onChanged()
      return true
    } catch (e) {
      setError(errorText(e, 'The draft could not be saved.'))
      return false
    } finally {
      setBusy('')
    }
  }

  const cut = async () => {
    if (!runtime.cutRelease) return
    if (dirty && !(await save())) return
    if (!window.confirm(`Cut release v${version}? The fixes are merged, tested, tagged and pushed, `
      + 'and every organization is told an update is available.')) return
    setBusy('cut')
    setError('')
    try {
      await runtime.cutRelease(release.id)
      notify?.success(`Release v${version} is being built.`)
      onChanged()
    } catch (e) {
      setError(errorText(e, 'The release could not be started.'))
    } finally {
      setBusy('')
    }
  }

  const discard = async () => {
    if (!runtime.discardRelease) return
    if (!window.confirm(`Discard draft v${release.version}? Its issues go back to "ready to release".`)) return
    setBusy('discard')
    setError('')
    try {
      await runtime.discardRelease(release.id)
      notify?.success('Draft discarded.')
      onChanged()
    } catch (e) {
      setError(errorText(e, 'The draft could not be discarded.'))
    } finally {
      setBusy('')
    }
  }

  const issueTitle = useMemo(() => {
    const m = new Map<number, string>()
    ;(release.issues || []).forEach((i) => m.set(i.number, i.title))
    return m
  }, [release.issues])

  return (
    <Card
      title={<>v{release.version} {release.title ? `— ${release.title}` : ''}</>}
      icon={<Tag size={15} />}
      actions={<Pill tone={releaseTone(release.status)}>{release.status === 'building' ? `building · ${release.progress?.step || ''}` : 'draft'}</Pill>}
    >
      {release.status === 'building' ? (
        <>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}><Spinner /> Building the release…</div>
          <ProgressLog release={release} />
        </>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '120px 120px 1fr', gap: 8, marginBottom: 10 }}>
            <div>
              <label style={labelStyle}>Version</label>
              <input value={version} disabled={!editable} style={inputStyle}
                onChange={(e) => { setVersion(e.target.value); setDirty(true) }} />
            </div>
            <div>
              <label style={labelStyle}>Kind</label>
              <select value={kind} disabled={!editable} style={inputStyle}
                onChange={(e) => { setKind(e.target.value); setDirty(true) }}>
                <option value="patch">Patch</option>
                <option value="minor">Minor</option>
                <option value="major">Major</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Title</label>
              <input value={title} disabled={!editable} style={inputStyle} maxLength={255}
                onChange={(e) => { setTitle(e.target.value); setDirty(true) }} />
            </div>
          </div>

          <div style={labelStyle}>What changed — one line per issue, as the organizations will read it</div>
          <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 10px' }}>
            {notes.map((n, i) => (
              <li key={`${n.number}-${i}`} style={{ display: 'grid', gridTemplateColumns: '60px 110px 1fr', gap: 6, marginBottom: 6, alignItems: 'start' }}>
                <span style={{ ...mutedText, fontWeight: 700, paddingTop: 6 }} title={issueTitle.get(n.number) || n.title || ''}>
                  #{n.number}
                </span>
                <select value={n.group || groups[groups.length - 1] || ''} disabled={!editable} style={inputStyle}
                  onChange={(e) => setNote(i, { group: e.target.value })}>
                  {groups.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
                <input value={n.note || ''} disabled={!editable} style={inputStyle} maxLength={500}
                  onChange={(e) => setNote(i, { note: e.target.value })} />
              </li>
            ))}
          </ul>

          <label style={labelStyle} htmlFor={`twsp-md-${release.id}`}>Release notes (markdown)</label>
          <textarea id={`twsp-md-${release.id}`} value={notesMd} disabled={!editable} rows={8}
            onChange={(e) => { setNotesMd(e.target.value); setMdTouched(true); setDirty(true) }}
            style={{ ...inputStyle, fontFamily: 'monospace', fontSize: '0.8425rem', resize: 'vertical' }} />
          {!mdTouched && editable && (
            <div style={mutedText}>Leave it untouched and it is rebuilt from the lines above when you save.</div>
          )}

          {release.error && <ErrorLine message={`The last attempt failed: ${release.error}`} />}
          <ErrorLine message={error} />

          {isPm && (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
              {runtime.discardRelease && (
                <button type="button" style={btn('danger')} disabled={!!busy} onClick={() => { void discard() }}>
                  {busy === 'discard' ? <Spinner size={13} /> : <Trash2 size={13} />} Discard
                </button>
              )}
              {editable && (
                <button type="button" style={btn('ghost')} disabled={!!busy || !dirty} onClick={() => { void save() }}>
                  {busy === 'save' ? <Spinner size={13} /> : <Save size={13} />} Save draft
                </button>
              )}
              {runtime.cutRelease && (
                <button type="button" style={btn('primary')} disabled={!!busy} onClick={() => { void cut() }}>
                  {busy === 'cut' ? <Spinner size={13} /> : <Rocket size={13} />} Cut release
                </button>
              )}
            </div>
          )}
        </>
      )}
    </Card>
  )
}

// ── history ────────────────────────────────────────────────────────────────

function HistoryRow({ release: r, meta, onOpenIssue }: {
  release: Release
  meta: SupportMeta
  onOpenIssue?: (n: number) => void
}) {
  const [open, setOpen] = useState(r.status === 'failed')
  const schema = (r.schema_changes || {}) as { destructive?: boolean }
  return (
    <li style={{ borderBottom: '1px solid var(--sp-border)', padding: '0.5rem 0' }}>
      <button type="button" onClick={() => setOpen((v) => !v)} style={{
        display: 'flex', alignItems: 'center', gap: 8, width: '100%', background: 'none', border: 'none',
        cursor: 'pointer', color: 'var(--sp-text)', padding: 0, textAlign: 'left',
      }}>
        {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        <strong>v{r.version}</strong>
        <Pill tone={releaseTone(r.status)}>{r.status}</Pill>
        {r.kind && <Pill>{r.kind}</Pill>}
        {r.status === 'released' && <PushPill release={r} />}
        {schema.destructive && <Pill tone="warning"><AlertTriangle size={10} /> schema change</Pill>}
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {r.title || ''}
        </span>
        <span style={mutedText}>{fmtDateTime(r.released_at || r.created_at)}</span>
      </button>
      {open && (
        <div style={{ paddingLeft: 21, marginTop: 6, fontSize: '0.8625rem' }}>
          {r.error && <ErrorLine message={r.error} />}
          {r.status === 'released' && <PushBanner release={r} />}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 6 }}>
            {r.tag && <span><Tag size={11} style={{ verticalAlign: -1 }} /> <code>{r.tag}</code></span>}
            {r.github_pr_url && (
              <a href={r.github_pr_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--sp-accent)' }}>
                <GitPullRequest size={11} style={{ verticalAlign: -1 }} /> Pull request
              </a>
            )}
            {r.github_release_url && (
              <a href={r.github_release_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--sp-accent)' }}>
                <ExternalLink size={11} style={{ verticalAlign: -1 }} /> GitHub release
              </a>
            )}
          </div>
          {r.notes_md && (
            <pre style={{
              whiteSpace: 'pre-wrap', margin: '0 0 6px', fontFamily: 'inherit', fontSize: '0.8625rem',
              background: 'var(--sp-bg2)', padding: '0.5rem', borderRadius: 6,
            }}>{r.notes_md}</pre>
          )}
          {(r.issues || []).length > 0 && (
            <ul style={{ margin: '0 0 6px', paddingLeft: 18 }}>
              {(r.issues || []).map((i) => (
                <li key={i.id}>
                  {onOpenIssue ? (
                    <button type="button" onClick={() => onOpenIssue(i.number)} style={{
                      background: 'none', border: 'none', padding: 0, color: 'var(--sp-accent)', cursor: 'pointer',
                    }}>#{i.number}</button>
                  ) : <>#{i.number}</>} {i.title}{' '}
                  <span style={mutedText}>({stateLabel(meta.lifecycle, i.status)})</span>
                </li>
              ))}
            </ul>
          )}
          <ProgressLog release={r} />
        </div>
      )}
    </li>
  )
}

// ── is the release where the live servers can get it? ─────────────────────

/**
 * Live servers upgrade by checking out a release's git TAG from the project's
 * repository, so "released" means nothing to them until the tag is pushed.
 * That is the one fact about a release that decides whether anyone can run
 * it, so it is shown on the row itself, not tucked into the details.
 */
function PushPill({ release: r }: { release: Release }) {
  if (r.push_status === 'pushed') {
    return <Pill tone="success" title={r.push_detail || ''}><Tag size={10} /> {r.tag || 'tag'} pushed</Pill>
  }
  if (r.push_status === 'failed') {
    return <Pill tone="error" title={r.push_detail || ''}><AlertTriangle size={10} /> push failed</Pill>
  }
  if (r.push_status === 'skipped') {
    return <Pill tone="warning" title={r.push_detail || ''}><AlertTriangle size={10} /> tag not pushed</Pill>
  }
  return null
}

function PushBanner({ release: r }: { release: Release }) {
  const ok = r.push_status === 'pushed'
  if (!r.push_status) return null
  const color = ok ? 'var(--sp-success)' : r.push_status === 'failed' ? 'var(--sp-error)' : 'var(--sp-warning)'
  return (
    <div role="status" style={{
      border: `1px solid ${color}`, borderRadius: 8, padding: '0.5rem 0.65rem', margin: '0 0 8px',
      fontSize: '0.8625rem',
    }}>
      <div style={{ fontWeight: 700, color }}>
        {ok
          ? <>Tag <code>{r.tag || `v${r.version}`}</code> is pushed — live servers can upgrade to v{r.version}.</>
          : r.push_status === 'failed'
            ? <>The tag could not be pushed — live servers cannot pull v{r.version} yet.</>
            : <>The tag was not pushed — live servers cannot pull v{r.version}.</>}
      </div>
      {r.push_detail && <div style={{ ...mutedText, marginTop: 2, whiteSpace: 'pre-wrap' }}>{r.push_detail}</div>}
      {!ok && (
        <div style={{ ...mutedText, marginTop: 4 }}>
          A live server upgrades by checking out the release's tag from the project's git repository. Until
          the tag is there, it keeps running its current version. {r.push_status === 'skipped'
            ? 'Link a repository (Advanced → Source control) and push the tag.'
            : 'Fix the repository access (token, branch protection) and push the tag again.'}
        </div>
      )}
    </div>
  )
}
